/* storybook-check-ignore */
import { Listing, ListingWithComputedProperties } from 'declarations/exclusives/listing';
import {
  useMemo,
  useState,
  createContext,
  useContext,
  useRef,
  ReactNode,
  useReducer,
  useCallback,
} from 'react';
import { Props as ReserveATourSlotButtonProps } from './ReserveATourSlotButton';
import ReserveATourModal, { ReserveATourState } from './ReserveATourModal';
import {
  getClosestSlots,
  getSlots,
  ExclusiveTourRequestTimeSlot,
  getInstantTimeSlots,
  ExclusiveTourRequest,
  REQUEST_TOUR_ERROR_MESSAGES,
  TimeSlotMoment,
  doesOpenHouseOverlapTimeSlot,
} from './util';
import useExclusiveAnalytics from 'components/exclusives/hooks/useExclusiveAnalytics';
import { AccountProps } from 'components/shared/AccountConnector';
import { doBFFPost, doGet } from 'components/api';
import { EXCLUSIVE_RAILS_HOST } from 'components/globals';
import useSWR from 'swr';
import isEmpty from 'lodash/isEmpty';
import take from 'lodash/take';
import {
  NotificationStatus,
  useNotificationSectionContext,
} from 'components/exclusives/NotificationSection';
import moment from 'moment-timezone';

const EXCLUSIVE_TOUR_REQUEST_PATH = 'exclusives/tour_request';

export function useRequestATour(
  listing: ListingWithComputedProperties,
  tourableListings: ListingWithComputedProperties[],
  account: AccountProps,
  isExclusivesRequestATourFFEnabled: boolean,
  { onError }: { onError?: (error: RequestATourError) => void } = {},
) {
  const isAuthenticated = !account.infoIsLoading && account.loggedIn;
  const ongoingOrUpcomingOpenHousesMomentRef = useRef<TimeSlotMoment[]>([]);
  const isPausedRef = useRef(false);
  const { data, error, mutate, isLoading } = useSWR<
    ExclusiveTourRequest | null | undefined,
    RequestATourError
  >(
    isExclusivesRequestATourFFEnabled && isAuthenticated && [EXCLUSIVE_TOUR_REQUEST_PATH, listing],
    getFetcher,
    {
      isPaused: () => isPausedRef.current,
      onError,
      shouldRetryOnError: false,
    },
  );
  isPausedRef.current = !!data;
  const [isMutating, toggleIsMutating] = useReducer((s) => !s, false);
  const canCreateTourRequest = (data === null || data === undefined) && !isMutating;
  const hasTourRequest = !!data;
  const { trackExclusivesEvent } = useExclusiveAnalytics();
  function reportEvent(label: string, additionalContextualData?: Record<string, unknown>) {
    trackExclusivesEvent('exclusives-request-a-tour', label, listing, additionalContextualData);
  }
  const isModalOpenedFromSingleButtonRef = useRef<boolean>();
  const slots = useMemo(() => getSlots(listing.timezone), [listing]);
  const [selectedSlots, setSelectedSlots] = useState<ExclusiveTourRequestTimeSlot[] | undefined>(
    undefined,
  );
  const isModalOpen = selectedSlots !== undefined;
  const { instantSlots, closestSlots } = useMemo(() => {
    const { instantSlots, ongoingOrUpcomingOpenHousesMoment } = getInstantTimeSlots(
      listing.exclusive_listing_info.ongoing_and_upcoming_open_houses,
      listing.timezone,
    );
    ongoingOrUpcomingOpenHousesMomentRef.current = ongoingOrUpcomingOpenHousesMoment;
    const closestSlots = getClosestSlots(
      slots,
      listing.exclusive_listing_info.ongoing_and_upcoming_open_houses,
      instantSlots,
      listing.timezone,
    );
    return {
      instantSlots,
      closestSlots: hasTourRequest
        ? take(instantSlots, 2).map((slot) => ({ slot, slotExists: true }))
        : closestSlots,
    };
  }, [slots, hasTourRequest]);
  const modalOpenedFromSingleButton = (val: boolean) => {
    isModalOpenedFromSingleButtonRef.current = val;
  };
  const openModal: ReserveATourSlotButtonProps['onClick'] = ({
    slotContentProps,
    contextualData,
  }) => {
    const slots = slotContentProps?.slot ? [slotContentProps.slot] : [];
    setSelectedSlots(slots);
    reportEvent('tour-request', {
      slots,
      ...contextualData,
    });
  };
  const isInstantSlot = useCallback((slot: ExclusiveTourRequestTimeSlot) => {
    const slotMoment = {
      startTime: moment(slot.start_time),
      endTime: moment(slot.end_time),
    };
    return ongoingOrUpcomingOpenHousesMomentRef.current.some((openHouse) =>
      doesOpenHouseOverlapTimeSlot(slotMoment, openHouse),
    );
  }, []);
  const closeModal = () => setSelectedSlots(undefined);
  const createTourRequest = () =>
    mutate(
      async () => {
        if (selectedSlots) {
          try {
            const selectedInstantSlots = selectedSlots.filter(isInstantSlot);
            const hasSelectedInstantSlots = selectedInstantSlots.length > 0;
            const canCreateTourRequestForInstantSpot =
              selectedSlots.length === 1 && hasSelectedInstantSlots;
            const payload = hasSelectedInstantSlots ? selectedInstantSlots : selectedSlots;

            if (canCreateTourRequest || canCreateTourRequestForInstantSpot) {
              toggleIsMutating();
              await postFetcher(listing, payload).finally(toggleIsMutating);
            } else {
              throw new RequestATourAlreadyRequestedError(selectedSlots);
            }
          } catch (e) {
            onError?.(e as RequestATourError);
          }
        }
        return null;
      },
      { revalidate: true, throwOnError: false },
    );
  return {
    listing,
    reportEvent,
    slots,
    selectedSlots,
    setSelectedSlots,
    instantSlots,
    closestSlots,
    isModalOpen,
    openModal,
    closeModal,
    isModalOpenedFromSingleButton: isModalOpenedFromSingleButtonRef.current,
    modalOpenedFromSingleButton,
    tourableListings,
    createTourRequest,
    canCreateTourRequest,
    data,
    hasTourRequest,
    isLoading: isLoading || isMutating,
    isError: !!error && !isLoading && !isMutating,
    error,
    isFeatureFlagEnabled: isExclusivesRequestATourFFEnabled,
    isButtonHidden: !closestSlots.length,
    isInitiallyLoadingRequestATour:
      account.infoIsLoading || (isAuthenticated && (isLoading || (data === undefined && !error))),
    isInstantSlot,
  };
}

class RequestATourResponseError extends Error {
  constructor(readonly response: Response, readonly body: any) {
    super('ResponseError');
  }

  static new(response: Response, body: any) {
    return {
      type: 'tourSpotUnavailable',
      cause: new RequestATourResponseError(response, body),
    };
  }
}

class RequestATourAlreadyRequestedError extends Error {
  constructor(readonly selectedSlots: ExclusiveTourRequestTimeSlot[]) {
    super('RequestATourAlreadyRequestedError');
  }

  static new(selectedSlots: ExclusiveTourRequestTimeSlot[]) {
    return {
      type: 'alreadyRequestedATour',
      cause: new RequestATourAlreadyRequestedError(selectedSlots),
    };
  }
}

class RequestATourGenericError extends Error {
  constructor(readonly cause: unknown) {
    super('RequestATourGenericError');
  }

  static new(cause: unknown) {
    return {
      type: 'generic',
      cause: new RequestATourGenericError(cause),
    };
  }
}

function exclusiveGetTourRequestPath(listing: Listing) {
  return `exclusives/${listing.slug}/tour-request`;
}

function exclusiveCreateTourRequestPath(listing: Listing) {
  return `exclusives/${listing.slug}/create_exclusive_tour_request`;
}

const getFetcher = async ([_, listing]: [key: string, listing: Listing]) => {
  let body: any, response: Response | undefined;
  try {
    response = await doGet(`/api/v1/${exclusiveGetTourRequestPath(listing)}`);
    body = response.status == 204 ? null : await response.json();
  } catch (e) {
    throw RequestATourGenericError.new(e);
  }
  if (response && !response.ok) {
    throw RequestATourResponseError.new(response, body);
  }
  return isEmpty(body) ? null : body;
};

const postFetcher = async (listing: Listing, selectedSlots: ExclusiveTourRequestTimeSlot[]) => {
  let body: any, response: Response | undefined;
  try {
    response = await doBFFPost(
      `${EXCLUSIVE_RAILS_HOST}/api/v1/${exclusiveCreateTourRequestPath(listing)}`,
      {
        tour_request_times: selectedSlots,
      },
    );
    body = response.status == 204 ? null : await response.json();
  } catch (e) {
    throw RequestATourGenericError.new(e);
  }
  if (response && !response.ok) {
    throw RequestATourResponseError.new(response, body);
  }
  return isEmpty(body) ? null : body;
};

const RequestATourContext = createContext({} as ReturnType<typeof useRequestATour>);

export const useRequestATourContext = () => useContext(RequestATourContext);

export function RequestATourProvider({
  listing,
  children,
  tourableListings,
  account,
  isExclusivesRequestATourFFEnabled,
}: {
  tourableListings: ListingWithComputedProperties[];
  listing: ListingWithComputedProperties;
  children?: ReactNode | undefined;
  account: AccountProps;
  isExclusivesRequestATourFFEnabled: boolean;
}) {
  const { displayNotification } = useNotificationSectionContext();
  const requestATour = useRequestATour(
    listing,
    tourableListings,
    account,
    isExclusivesRequestATourFFEnabled,
    {
      onError: (err) => {
        const errorMessage =
          REQUEST_TOUR_ERROR_MESSAGES[err.type] || REQUEST_TOUR_ERROR_MESSAGES.generic;
        displayNotification(NotificationStatus.Error, errorMessage);
      },
    },
  );
  return (
    <RequestATourContext.Provider value={requestATour}>
      {children}
      {requestATour.selectedSlots && (
        <ReserveATourModal
          initialSelectingSpotState={
            requestATour.isModalOpenedFromSingleButton
              ? ReserveATourState.SelectSingleSpot
              : ReserveATourState.SelectingSlots
          }
          closestSlots={requestATour.closestSlots}
          isOpen={requestATour.isModalOpen}
          onRequestClose={requestATour.closeModal}
          setSelectedSlots={requestATour.setSelectedSlots}
          selectedSlots={requestATour.selectedSlots}
          instantSlots={requestATour.instantSlots}
          slots={requestATour.slots}
          reportEvent={requestATour.reportEvent}
          listing={listing}
          tourableListings={tourableListings}
          canCreateTourRequest={requestATour.canCreateTourRequest}
          createTourRequest={requestATour.createTourRequest}
          isTourRequestLoading={requestATour.isLoading}
          isTourRequestError={requestATour.isError}
          tourRequestData={requestATour.data}
          error={requestATour.error}
          hasTourRequest={requestATour.hasTourRequest}
          isInstantSlot={requestATour.isInstantSlot}
        />
      )}
    </RequestATourContext.Provider>
  );
}

export type RequestATourError =
  | {
      type: 'generic';
      cause: RequestATourGenericError;
    }
  | {
      type: 'tourSpotUnavailable';
      cause: RequestATourResponseError;
    }
  | {
      type: 'alreadyRequestedATour';
      cause: RequestATourAlreadyRequestedError;
    };
